import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import StorefrontAssetSlot from "@/components/admin/StorefrontAssetSlot";

const ASSET_SPECS = [
    {
        key: "logo_url",
        label: "Header / sidebar logo",
        testId: "logo",
        spec: {
            accept: "image/png",
            acceptLabel: "PNG with transparency",
            minWidth: 600,
            minHeight: 200,
            aspectMin: 2,
            aspectMax: 4,
            preferredKb: 200,
            recommendation:
                "PNG with transparency · min 600 × 200 px · aspect 2:1 to 4:1 · prefer < 200 KB",
        },
    },
    {
        key: "favicon_url",
        label: "Favicon",
        testId: "favicon",
        spec: {
            accept: "image/png",
            acceptLabel: "PNG",
            exactWidth: 512,
            exactHeight: 512,
            preferredKb: 50,
            recommendation: "512 × 512 PNG (square crop of mark only) · prefer < 50 KB",
        },
    },
    {
        key: "hero_desktop_url",
        label: "Hero banner — desktop",
        testId: "hero-desktop",
        spec: {
            accept: "image/jpeg",
            acceptLabel: "JPG",
            exactWidth: 1920,
            exactHeight: 900,
            preferredKb: 350,
            recommendation: "1920 × 900 px JPG · quality 70-85 · prefer < 350 KB",
        },
    },
    {
        key: "hero_mobile_url",
        label: "Hero banner — mobile",
        testId: "hero-mobile",
        spec: {
            accept: "image/jpeg",
            acceptLabel: "JPG",
            exactWidth: 750,
            exactHeight: 1000,
            preferredKb: 250,
            recommendation:
                "750 × 1000 px JPG · portrait crop or focal-left subject · prefer < 250 KB",
        },
    },
    {
        key: "category_tile_default_url",
        label: "Category tile (default)",
        testId: "category-tile",
        spec: {
            accept: "image/jpeg",
            acceptLabel: "JPG",
            exactWidth: 900,
            exactHeight: 900,
            preferredKb: 200,
            recommendation: "900 × 900 px JPG · prefer < 200 KB · used as fallback when a category has no image",
        },
    },
    {
        key: "product_default_url",
        label: "Product image (default)",
        testId: "product-default",
        spec: {
            accept: "image/jpeg,image/png",
            acceptLabel: "JPG or PNG",
            exactWidth: 1200,
            exactHeight: 1200,
            preferredKb: 500,
            maxKb: 6 * 1024,
            recommendation:
                "1200 × 1200 px JPG/PNG · prefer < 500 KB (max 6 MB) · fallback when a product has no images",
        },
    },
    {
        key: "instagram_tile_default_url",
        label: "Instagram tile (default)",
        testId: "instagram-tile",
        spec: {
            accept: "image/jpeg",
            acceptLabel: "JPG",
            exactWidth: 1080,
            exactHeight: 1080,
            preferredKb: 250,
            recommendation: "1080 × 1080 px JPG · prefer < 250 KB · fallback for Instagram tiles without an image",
        },
    },
];

export default function AdminSettings() {
    const { refresh } = useSettings();
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/settings").then((r) => setS(r.data));
    }, []);

    if (!s) return <div className="text-white/55">Loading…</div>;
    const set = (k, v) => setS({ ...s, [k]: v });

    // Persist a single asset URL immediately so admins don't lose uploads if they navigate away.
    const setAssetAndPersist = async (key, url) => {
        setS((prev) => ({ ...prev, [key]: url }));
        try {
            await api.put("/admin/settings", { [key]: url });
            refresh();
        } catch (e) {
            toast.error(`Could not save ${key}`);
        }
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            // Send EVERY editable field on this page so nothing silently drops.
            // Backend will ignore null values via its model_dump filter.
            const payload = {
                // Branding
                store_name: s.store_name ?? "",
                tagline: s.tagline ?? "",
                contact_email: s.contact_email ?? "",
                contact_phone: s.contact_phone ?? "",
                address: s.address ?? "",
                currency_symbol: s.currency_symbol ?? "Rs.",
                logo_url: s.logo_url ?? "",
                // Storefront assets
                favicon_url: s.favicon_url ?? "",
                hero_desktop_url: s.hero_desktop_url ?? "",
                hero_mobile_url: s.hero_mobile_url ?? "",
                category_tile_default_url: s.category_tile_default_url ?? "",
                product_default_url: s.product_default_url ?? "",
                instagram_tile_default_url: s.instagram_tile_default_url ?? "",
                // Social
                instagram_url: s.instagram_url ?? "",
                instagram_handle: s.instagram_handle ?? "",
                instagram_section_enabled: !!s.instagram_section_enabled,
                facebook_url: s.facebook_url ?? "",
                youtube_url: s.youtube_url ?? "",
                whatsapp_number: s.whatsapp_number ?? "",
                // Notifications  (THE missing block that caused 'Save still fails')
                admin_notification_email: s.admin_notification_email ?? "",
                email_sender_name: s.email_sender_name ?? "",
                email_from_address: s.email_from_address ?? "",
                email_provider: s.email_provider ?? "none",
                notifications_enabled: !!s.notifications_enabled,
                notify_customer_on_status: !!s.notify_customer_on_status,
                notify_admin_on_new_order: !!s.notify_admin_on_new_order,
            };
            const { data } = await api.put("/admin/settings", payload);
            // Hydrate state from server response so what user sees == what's stored.
            setS(data);
            setLastSavedAt(new Date());
            toast.success("Settings saved");
            refresh();
        } catch (e) {
            const msg = formatApiError(e?.response?.data?.detail) || e?.message || "Save failed";
            setError(msg);
            toast.error(`Save failed: ${msg}`);
            // eslint-disable-next-line no-console
            console.error("Settings save failed", e?.response?.status, e?.response?.data, e);
        }
        setSaving(false);
    };

    return (
        <div className="space-y-5 max-w-4xl" data-testid="admin-settings-page">
            <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Store</div>
                <h1 className="font-display text-3xl font-black uppercase tracking-tight">Settings</h1>
            </div>

            <Card title="Branding">
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Store name"><input value={s.store_name || ""} onChange={(e) => set("store_name", e.target.value)} className="ost-input" data-testid="set-store-name" /></Field>
                    <Field label="Tagline"><input value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="ost-input" data-testid="set-tagline" /></Field>
                    <Field label="Currency symbol"><input value={s.currency_symbol || ""} onChange={(e) => set("currency_symbol", e.target.value)} className="ost-input" data-testid="set-currency" /></Field>
                </div>
            </Card>

            <Card title="Storefront Assets">
                <p className="text-[12px] text-white/55 mb-3">
                    Upload high-quality assets for the public storefront. Each slot validates file
                    type, dimensions and warns if above the recommended size. Existing fallback
                    images are used when a slot is empty.
                </p>
                <div className="grid md:grid-cols-2 gap-3" data-testid="storefront-assets-grid">
                    {ASSET_SPECS.map((a) => (
                        <StorefrontAssetSlot
                            key={a.key}
                            label={a.label}
                            value={s[a.key] || ""}
                            spec={a.spec}
                            testId={a.testId}
                            onChange={(url) => setAssetAndPersist(a.key, url)}
                        />
                    ))}
                </div>
            </Card>

            <Card title="Contact">
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Contact email"><input value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="ost-input" data-testid="set-email" /></Field>
                    <Field label="Contact phone"><input value={s.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="ost-input" data-testid="set-phone" /></Field>
                    <Field label="WhatsApp number"><input value={s.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} className="ost-input" data-testid="set-whatsapp" placeholder="+91XXXXXXXXXX" /><p className="text-[11px] text-white/45 mt-1">Include country code, for example +91XXXXXXXXXX</p></Field>
                    <Field label="Address"><input value={s.address || ""} onChange={(e) => set("address", e.target.value)} className="ost-input" data-testid="set-address" /></Field>
                </div>
            </Card>

            <Card title="Social — Instagram &amp; more">
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Instagram URL"><input value={s.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} className="ost-input" data-testid="set-ig-url" /></Field>
                    <Field label="Instagram handle (e.g. @opensea)"><input value={s.instagram_handle || ""} onChange={(e) => set("instagram_handle", e.target.value)} className="ost-input" data-testid="set-ig-handle" /></Field>
                    <Field label="Facebook URL"><input value={s.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} className="ost-input" data-testid="set-fb" /></Field>
                    <Field label="YouTube URL"><input value={s.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)} className="ost-input" data-testid="set-yt" /></Field>
                </div>
                <label className="inline-flex items-center gap-2 text-sm mt-4">
                    <input type="checkbox" checked={!!s.instagram_section_enabled} onChange={(e) => set("instagram_section_enabled", e.target.checked)} className="accent-[#00BCE5]" data-testid="set-ig-toggle" />
                    Show "Follow us on Instagram" section on homepage
                </label>
                <p className="text-xs text-white/55 mt-2">
                    Manage homepage Instagram showcase tiles in the <a href="/admin/instagram" className="text-[#00BCE5] hover:underline">Instagram</a> section.
                </p>
            </Card>

            <Card title="Notifications">
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Admin notification email *">
                        <input value={s.admin_notification_email || ""} onChange={(e) => set("admin_notification_email", e.target.value)} className="ost-input" data-testid="set-admin-notif-email" placeholder="orders@yourstore.com" />
                    </Field>
                    <Field label="Email sender name">
                        <input value={s.email_sender_name || ""} onChange={(e) => set("email_sender_name", e.target.value)} className="ost-input" data-testid="set-sender-name" placeholder="Opensea Fishing Tackles" />
                    </Field>
                    <Field label="From address (must be a verified domain)">
                        <input value={s.email_from_address || ""} onChange={(e) => set("email_from_address", e.target.value)} className="ost-input" data-testid="set-from-address" placeholder="orders@yourdomain.com" />
                    </Field>
                    <Field label="Email provider">
                        <select value={s.email_provider || "none"} onChange={(e) => set("email_provider", e.target.value)} className="ost-input" data-testid="set-email-provider">
                            <option value="none">None (queue only)</option>
                            <option value="brevo">Brevo (recommended)</option>
                            <option value="resend">Resend</option>
                        </select>
                    </Field>
                </div>
                <div className="mt-3 space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!s.notifications_enabled} onChange={(e) => set("notifications_enabled", e.target.checked)} className="accent-[#00BCE5]" data-testid="set-notif-enabled" />
                        Enable notifications globally
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!s.notify_admin_on_new_order} onChange={(e) => set("notify_admin_on_new_order", e.target.checked)} className="accent-[#00BCE5]" data-testid="set-notif-admin" />
                        Email admin on every new order
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!s.notify_customer_on_status} onChange={(e) => set("notify_customer_on_status", e.target.checked)} className="accent-[#00BCE5]" data-testid="set-notif-customer" />
                        Email customer on order status changes (placed/confirmed/packed/shipped/delivered)
                    </label>
                </div>
                <div className="mt-4 text-[11px] text-white/55 leading-relaxed bg-[#0A0A0B] border border-[#27272A] rounded-sm p-3">
                    <div className="font-display font-semibold uppercase tracking-widest text-[#00BCE5] mb-1">Live sending status</div>
                    <p>Notification architecture is fully wired with two provider options:</p>
                    <ul className="mt-1 list-disc list-inside space-y-0.5 text-white/65">
                        <li><strong className="text-white">Brevo</strong> — works with Single Sender Verification (no DNS needed). Set provider to <strong>Brevo</strong> and ensure <code className="text-[#00BCE5]">BREVO_API_KEY</code> is set on the backend.</li>
                        <li><strong className="text-white">Resend</strong> — requires a verified sending domain. Set provider to <strong>Resend</strong> and ensure <code className="text-[#00BCE5]">RESEND_API_KEY</code> is set on the backend.</li>
                    </ul>
                    <p className="mt-2">When provider is <strong>None</strong>, every notification is recorded with status <span className="text-[#FF9F0A]">pending_credentials</span> and can be retried after switching to a live provider. WhatsApp and SMS channels follow the same architecture.</p>
                </div>
            </Card>

            <button
                disabled={saving}
                onClick={save}
                className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-5 py-3 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50"
                data-testid="set-save"
            >
                <Save size={16} /> {saving ? "Saving…" : "Save settings"}
            </button>
            {error && (
                <div className="text-sm text-[#FF6E66] bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-sm px-4 py-2 mt-2" data-testid="set-save-error">
                    {error}
                </div>
            )}
            {lastSavedAt && !error && (
                <div className="text-xs text-white/55 mt-2" data-testid="set-save-status">
                    Last saved {lastSavedAt.toLocaleTimeString()}
                </div>
            )}

            <style>{`
                .ost-input {
                    width: 100%;
                    background: #0A0A0B;
                    border: 1px solid #27272A;
                    border-radius: 4px;
                    padding: 10px 12px;
                    color: white;
                    outline: none;
                }
                .ost-input:focus { border-color: #00BCE5; }
            `}</style>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <section className="bg-[#18181B] border border-[#27272A] rounded-sm">
            <div className="px-4 py-3 border-b border-[#27272A] font-display uppercase font-black tracking-tight" dangerouslySetInnerHTML={{ __html: title }} />
            <div className="p-4 space-y-2">{children}</div>
        </section>
    );
}
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1.5">{label}</label>
            {children}
        </div>
    );
}
